/**
 * ClaimPage — /claim/:code and /claim?code=  (PRI-456)
 *
 * Human side of the device-code key-claim flow. An AI agent calls
 * POST /v1/keys/request, gets back a claim_url, and opens it in a browser.
 * The user signs in and clicks Approve → POST /v1/keys/claim/:code mints a
 * live key delivered to the waiting agent. The page never shows the raw key.
 *
 * Uses usePrimateAuth() (not useAuth()) so it works in both AppRoutesWithAuth
 * and AppRoutesNoAuth — including when Clerk is not configured (test env).
 */

import { useState, useEffect } from 'react'
import { useParams, useSearchParams, useLocation, Navigate } from 'react-router'
import { usePrimateAuth } from '../primate/auth/usePrimateAuth'
import { claimDeviceCode } from '../primate/api/keys'
import { trackClaimViewed, trackClaimApproved, trackClaimFailed } from '../lib/analytics'

// ── Brand palette — forced light mode (brand-guidelines-v3.2) ─────────────────
const L = {
  bgPage:       '#F2F0EB',
  bgSurface:    '#FAFAF7',
  border:       '#D4D0C8',
  textPrimary:  '#1A1916',
  textMuted:    '#7A7669',
  signal:       '#1B4FE0',
  signalBg:     '#EEF2FF',
  danger:       '#DC2626',
  dangerBg:     '#FEF2F2',
  dangerBorder: '#FECACA',
  success:      '#16A34A',
  successBg:    '#F0FDF4',
  successBorder:'#BBF7D0',
}

type PageState = 'loading' | 'ready' | 'approving' | 'approved' | 'denied' | 'error'

function maskCode(code: string): string {
  if (code.length <= 8) return code
  return `${code.slice(0, 4)}...${code.slice(-4)}`
}

// ── LogoMark ──────────────────────────────────────────────────────────────────
function LogoMark() {
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <img
        src="/Darwin_v3_vinyl_transparent.png"
        alt=""
        style={{ height: 32, width: 'auto', objectFit: 'contain', marginRight: 10 }}
      />
      <div style={{ lineHeight: 1.15 }}>
        <span style={{ fontSize: 18, fontWeight: 700, color: L.textPrimary, letterSpacing: '-0.03em' }}>
          Primate
        </span>
        <span style={{ fontSize: 18, fontWeight: 700, color: L.signal, letterSpacing: '-0.03em' }}>
          Intelligence
        </span>
      </div>
    </div>
  )
}

export default function ClaimPage() {
  // ── All hooks unconditionally at the top (no early returns before useEffect) ─
  const { code: pathCode } = useParams<{ code?: string }>()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  // usePrimateAuth is context-safe: works inside ClerkProvider and NoAuthProvider
  const { isSignedIn, authLoaded } = usePrimateAuth()
  const code = pathCode ?? searchParams.get('code') ?? ''

  const [state, setState] = useState<PageState>('loading')
  const [errorMsg, setErrorMsg] = useState('')

  const returnUrl = encodeURIComponent(location.pathname + location.search)
  const shouldRedirectToLogin = authLoaded && !isSignedIn

  useEffect(() => {
    if (!authLoaded || shouldRedirectToLogin) return
    if (!code) {
      setState('error')
      setErrorMsg('No device code found in this URL.')
      return
    }
    setState('ready')
    trackClaimViewed({ code })
  }, [authLoaded, shouldRedirectToLogin, code])

  // ── Auth / loading guards (after all hooks) ───────────────────────────────
  // Order matters: check auth-not-loaded first, then redirect, then page-loading.
  if (!authLoaded) {
    return (
      <div style={{ minHeight: '100vh', background: L.bgPage, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ color: L.textMuted, fontSize: 14 }}>Loading…</div>
      </div>
    )
  }

  if (shouldRedirectToLogin) {
    return <Navigate to={`/login?redirect_url=${returnUrl}`} replace />
  }

  if (state === 'loading') {
    return (
      <div style={{ minHeight: '100vh', background: L.bgPage, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif' }}>
        <div style={{ color: L.textMuted, fontSize: 14 }}>Loading…</div>
      </div>
    )
  }

  // ── Handlers ──────────────────────────────────────────────────────────────
  async function handleApprove() {
    if (!code) return
    setState('approving')
    try {
      await claimDeviceCode(code)
      setState('approved')
      trackClaimApproved()
    } catch (err) {
      const e = err as { code?: string; status?: number; message?: string }
      const isExpired =
        e.status === 404 || e.status === 410 ||
        e.code === 'device_code_expired' || e.code === 'resource_not_found'
      setErrorMsg(
        isExpired
          ? 'This request has expired or was already used. Ask your agent to start a new request.'
          : (e.message ?? 'Something went wrong. Please try again.')
      )
      setState('error')
      trackClaimFailed({ reason: e.code ?? e.message ?? 'unknown' })
    }
  }

  function handleDeny() {
    setState('denied')
  }

  // ── Styles ────────────────────────────────────────────────────────────────
  const card: React.CSSProperties = {
    background: L.bgSurface,
    border: `1px solid ${L.border}`,
    borderRadius: 8,
    padding: '40px 36px',
    maxWidth: 480,
    width: '100%',
    boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
  }

  const btn = (variant: 'primary' | 'danger', disabled?: boolean): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 20px',
    borderRadius: 6,
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
    transition: 'background 150ms',
    ...(variant === 'primary' ? {
      background: L.signal,
      color: '#fff',
      border: 'none',
    } : {
      background: 'transparent',
      color: L.danger,
      border: `1px solid ${L.dangerBorder}`,
    }),
  })

  const label: React.CSSProperties = {
    fontFamily: 'Inter, sans-serif',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: L.textMuted,
    marginBottom: 6,
  }

  const codePill: React.CSSProperties = {
    fontFamily: 'ui-monospace, "Cascadia Code", monospace',
    fontSize: 14,
    background: L.bgPage,
    border: `1px solid ${L.border}`,
    borderRadius: 4,
    padding: '6px 12px',
    color: L.textPrimary,
    display: 'inline-block',
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: '100vh',
        background: L.bgPage,
        fontFamily: 'Inter, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '48px 16px 80px',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: 40 }}>
        <LogoMark />
      </div>

      {/* ── Approved ── */}
      {state === 'approved' && (
        <div style={card}>
          <div
            style={{
              background: L.successBg,
              border: `1px solid ${L.successBorder}`,
              borderRadius: 6,
              padding: '16px 20px',
              marginBottom: 24,
            }}
          >
            <div style={{ fontSize: 20, marginBottom: 6 }}>✓</div>
            <div style={{ fontWeight: 700, fontSize: 16, color: L.success, marginBottom: 4 }}>Key issued</div>
            <div style={{ fontSize: 14, color: L.textMuted, lineHeight: 1.6 }}>
              Return to your agent — it will receive the key within a few seconds.
            </div>
          </div>
          <div style={{ fontSize: 13, color: L.textMuted, lineHeight: 1.6 }}>
            You can view and manage your API keys any time in{' '}
            <a href="/dashboard/api-keys" style={{ color: L.signal, textDecoration: 'none' }}>
              Dashboard → API Keys
            </a>
            .
          </div>
        </div>
      )}

      {/* ── Denied ── */}
      {state === 'denied' && (
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 18, color: L.textPrimary, marginBottom: 12 }}>
            Request dismissed
          </div>
          <div style={{ fontSize: 14, color: L.textMuted, lineHeight: 1.6 }}>
            The key request was not approved. Your agent will continue waiting — ask it to
            start a new request if you change your mind.
          </div>
        </div>
      )}

      {/* ── Error ── */}
      {state === 'error' && (
        <div style={card}>
          <div
            style={{
              background: L.dangerBg,
              border: `1px solid ${L.dangerBorder}`,
              borderRadius: 6,
              padding: '16px 20px',
              marginBottom: 20,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 15, color: L.danger, marginBottom: 4 }}>
              Unable to approve
            </div>
            <div style={{ fontSize: 14, color: L.textMuted, lineHeight: 1.6 }}>
              {errorMsg || 'Something went wrong.'}
            </div>
          </div>
          <div style={{ fontSize: 13, color: L.textMuted, lineHeight: 1.6 }}>
            Ask your agent to run{' '}
            <code style={{ ...codePill, fontSize: 12 }}>POST /v1/keys/request</code>
            {' '}again to get a fresh link.
          </div>
        </div>
      )}

      {/* ── Ready / Approving ── */}
      {(state === 'ready' || state === 'approving') && (
        <div style={card}>
          {/* Intent description */}
          <div style={{ marginBottom: 28 }}>
            <div style={{ fontWeight: 700, fontSize: 20, color: L.textPrimary, marginBottom: 10, lineHeight: 1.3 }}>
              API key request
            </div>
            <div style={{ fontSize: 14, color: L.textMuted, lineHeight: 1.65 }}>
              An AI agent is requesting a live{' '}
              <strong style={{ color: L.textPrimary }}>Primate Vision API key</strong>{' '}
              for your account. Approving will issue a new key delivered directly to the agent.
            </div>
          </div>

          {/* Device code */}
          <div style={{ marginBottom: 28 }}>
            <div style={label}>Request code</div>
            <div style={codePill} data-testid="device-code">{maskCode(code)}</div>
          </div>

          {/* Security note */}
          <div
            style={{
              background: L.signalBg,
              border: `1px solid #C7D7FF`,
              borderRadius: 6,
              padding: '12px 16px',
              marginBottom: 28,
              fontSize: 13,
              color: L.textMuted,
              lineHeight: 1.6,
            }}
          >
            Only approve if <em>you</em> initiated this request in your agent. Keys issued here
            can make live API calls on your account.
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <button
              data-testid="approve-btn"
              style={btn('primary', state === 'approving')}
              disabled={state === 'approving'}
              onClick={handleApprove}
            >
              {state === 'approving' ? 'Approving…' : 'Approve'}
            </button>
            <button
              data-testid="deny-btn"
              style={btn('danger', state === 'approving')}
              disabled={state === 'approving'}
              onClick={handleDeny}
            >
              Deny
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
